/**
 * O cronograma mais claro — as linhas (onda 378, spec 15 §3.3).
 *
 * «Quando o ensaio é lançado em um equipamento, ele fica mais à esquerda do que
 *  o nome do equipamento […] parece que quebra os níveis hierárquicos. Permita
 *  encolher um tipo de equipamento também. […] Para métodos únicos, como
 *  caracterização, na linha da amostra programada para um ensaio coloque apenas
 *  o nome da amostra (nomenclatura cliente) e cliente, remova a nomenclatura
 *  única da amostra das linhas do cronograma.» (Humberto, 03/11/2026)
 *
 * Funções PURAS e sem importação (o teste 362 as prova sem navegador).
 */

/** Os três níveis da grade: grupo › equipamento ou frente › amostra. */
export type NivelDaLinha = 'grupo' | 'recurso' | 'amostra';

/**
 * O recuo de cada nível, em px, da borda da coluna dos nomes até onde o nível
 * começa: grupo 4 · equipamento/frente 24 · amostra 52 (spec 15 §3.3). O ▸ do
 * recolher mora DENTRO do recuo do nível (20 px + 4 de vão), por isso o nome do
 * equipamento começa em 48 e o da amostra, em 52 — nunca à esquerda do pai.
 */
export function recuoDoNivel(nivel: NivelDaLinha): number {
  if (nivel === 'grupo') return 4;
  if (nivel === 'recurso') return 24;
  return 52;
}

/** A chave de quem está recolhido: um grupo, um equipamento ou uma frente. */
export function chaveDeRecolher(tipo: 'grupo' | 'equipamento' | 'frente', id: string): string {
  return `${tipo}:${id}`;
}

/**
 * O rótulo da linha da amostra: «nomenclatura do cliente · cliente». A TAG do
 * método vai na frente só quando a frente reúne mais de um método (GRAN, SCS,
 * AZUL, CRUMB…); nas máquinas especiais, a TAG fica na barra. O código AM sai da
 * linha — fica na dica e no painel do ensaio. Amostra que chegou sem nome do
 * cliente aparece pelo código, que é o único nome que ela tem.
 */
export function rotuloDaLinhaDaAmostra(
  e: { amostra_identificacao: string | null; amostra_codigo: string; cliente_curto: string; metodo_codigo: string },
  frenteTemVariosMetodos: boolean,
): string {
  const nome = (e.amostra_identificacao ?? '').trim() || e.amostra_codigo;
  const partes = [nome, e.cliente_curto].filter(Boolean);
  return (frenteTemVariosMetodos ? [e.metodo_codigo, ...partes] : partes).join(' · ');
}

/**
 * O cliente em poucas letras, para caber na linha (0191): sem a forma jurídica
 * («LTDA», «S/A», «ME»…) e com no máximo três palavras. O nome inteiro fica na dica.
 */
export function clienteCurto(nome: string | null | undefined): string {
  if (!nome) return 'sem cliente';
  const semForma = nome
    .replace(/\b(ltda\.?|s\.?\/?a\.?|me|epp|eireli|s\.?s\.?|cia\.?|ltda me)\b\.?/gi, '')
    .replace(/[\s\-–]+$/g, '').trim();
  const palavras = semForma.split(/\s+/).filter(Boolean);
  const curto = palavras.slice(0, 3).join(' ');
  return curto.length > 26 ? `${curto.slice(0, 25)}…` : curto;
}
